import React, { useState, useEffect } from 'react';
import { 
  Hotel, 
  Plus, 
  Trash2, 
  Edit, 
  Calendar, 
  MapPin, 
  DollarSign, 
  Save, 
  X, 
  Search, 
  Filter, 
  AlertCircle,
  Building,
  Home,
  Bed,
  Star
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';

interface Accommodation {
  id: string;
  trip_id: string;
  name: string;
  type: 'hotel' | 'apartment' | 'house' | 'hostel' | 'resort' | 'other';
  address: string;
  check_in_date: string;
  check_out_date: string;
  confirmation_number?: string;
  total_cost?: number;
  currency?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface Trip {
  id: string;
  title: string;
  destination?: string;
}

interface AccommodationsProps {
  tripId?: string;
}

export function Accommodations({ tripId }: AccommodationsProps) {
  const { user } = useAuth();
  const [accommodations, setAccommodations] = useState<Accommodation[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [tripFilter, setTripFilter] = useState<string>(tripId || 'all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAccommodation, setSelectedAccommodation] = useState<Accommodation | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    trip_id: tripId || '',
    name: '',
    type: 'hotel' as const,
    address: '',
    check_in_date: '',
    check_out_date: '',
    confirmation_number: '',
    total_cost: '',
    currency: 'AUD',
    notes: ''
  });

  const typeOptions = [
    { value: 'hotel', label: 'Hotel', icon: <Building size={16} /> },
    { value: 'apartment', label: 'Apartment', icon: <Home size={16} /> },
    { value: 'house', label: 'House', icon: <Home size={16} /> },
    { value: 'hostel', label: 'Hostel', icon: <Bed size={16} /> },
    { value: 'resort', label: 'Resort', icon: <Star size={16} /> },
    { value: 'other', label: 'Other', icon: <Hotel size={16} /> }
  ];

  const currencyOptions = ['AUD', 'USD', 'EUR', 'GBP', 'CAD', 'JPY'];

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Load trips first (only if we need to show trip filter)
        if (!tripId) {
          const { data: tripsData, error: tripsError } = await supabase
            .from('trips')
            .select('id, title, destination')
            .eq('user_id', user.id)
            .order('start_date', { ascending: true });
            
          if (tripsError) throw tripsError;
          setTrips(tripsData || []);
        }

        // Load accommodations - filter by tripId if provided, otherwise all for user
        // Handle case where user_id column might not exist yet (before migration runs)
        let accommodationsQuery = supabase
          .from('accommodations')
          .select('*');
          
        // Try to filter by user_id, fall back to trip_id if column doesn't exist
        if (tripId) {
          accommodationsQuery = accommodationsQuery.eq('trip_id', tripId);
        } else {
          // Try user_id first, fall back to trip_id filtering
          try {
            accommodationsQuery = accommodationsQuery.eq('user_id', user.id);
          } catch (err: any) {
            // If user_id column doesn't exist, filter by trip_id from user's trips
            if (trips.length > 0) {
              accommodationsQuery = accommodationsQuery.in('trip_id', trips.map(t => t.id));
            } else {
              // No trips, return empty array
              setAccommodations([]);
              return;
            }
          }
        }
          
        accommodationsQuery = accommodationsQuery.order('check_in_date', { ascending: true });
        const { data: accommodationsData, error: accommodationsError } = await accommodationsQuery;
        
        // If error is about missing user_id column, try fallback approach
        if (accommodationsError && accommodationsError.code === '42703' && accommodationsError.message?.includes('user_id')) {
          // Column doesn't exist, use trip-based filtering
          let fallbackQuery = supabase
            .from('accommodations')
            .select('*');
          
          if (tripId) {
            fallbackQuery = fallbackQuery.eq('trip_id', tripId);
          } else if (trips.length > 0) {
            fallbackQuery = fallbackQuery.in('trip_id', trips.map(t => t.id));
          } else {
            setAccommodations([]);
            return;
          }
          
          const { data: fallbackData, error: fallbackError } = await fallbackQuery.order('check_in_date', { ascending: true });
          if (fallbackError) throw fallbackError;
          setAccommodations(fallbackData || []);
          return;
        }
          
        if (accommodationsError) throw accommodationsError;
        setAccommodations(accommodationsData || []);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user, tripId, trips.length]);

  const handleAddAccommodation = () => {
    setFormData({
      trip_id: '',
      name: '',
      type: 'hotel',
      address: '',
      check_in_date: '',
      check_out_date: '',
      confirmation_number: '',
      total_cost: '',
      currency: 'AUD',
      notes: ''
    });
    setIsEditing(false);
    setShowAddModal(true);
  };

  const handleEditAccommodation = (accommodation: Accommodation) => {
    setSelectedAccommodation(accommodation);
    setFormData({
      trip_id: accommodation.trip_id,
      name: accommodation.name,
      type: accommodation.type,
      address: accommodation.address,
      check_in_date: accommodation.check_in_date,
      check_out_date: accommodation.check_out_date,
      confirmation_number: accommodation.confirmation_number || '',
      total_cost: accommodation.total_cost?.toString() || '',
      currency: accommodation.currency || 'AUD',
      notes: accommodation.notes || ''
    });
    setIsEditing(true);
    setShowAddModal(true);
  };

  const handleViewAccommodation = (accommodation: Accommodation) => {
    setSelectedAccommodation(accommodation);
    setShowViewModal(true);
  };

  const handleDeleteAccommodation = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this accommodation? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('accommodations')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        
        setAccommodations(accommodations.filter(acc => acc.id !== id));
      } catch (err) {
        console.error('Error deleting accommodation:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete accommodation');
      }
    }
  };

  const handleSaveAccommodation = async () => {
    // Validate form
    if (!formData.trip_id || !formData.name || !formData.address || !formData.check_in_date || !formData.check_out_date) {
      setError('Please fill in all required fields');
      return;
    }

    if (new Date(formData.check_in_date) > new Date(formData.check_out_date)) {
      setError('Check-out date must be after check-in date');
      return;
    }

    try {
      const accommodationData: any = {
        trip_id: formData.trip_id || null,
        name: formData.name,
        type: formData.type,
        address: formData.address,
        check_in_date: formData.check_in_date,
        check_out_date: formData.check_out_date,
        confirmation_number: formData.confirmation_number || null,
        currency: formData.currency,
        notes: formData.notes || null
      };

      // Add user_id if column exists, otherwise use total_cost if total_price doesn't exist
      try {
        // Try to add user_id (will fail silently if column doesn't exist)
        accommodationData.user_id = user.id;
      } catch (err) {
        // Column doesn't exist, skip it
      }

      // Use total_price if it exists, otherwise use total_cost for backward compatibility
      if (formData.total_cost) {
        const cost = parseFloat(formData.total_cost);
        accommodationData.total_price = cost;
        accommodationData.total_cost = cost; // For backward compatibility
      }

      if (isEditing && selectedAccommodation) {
        const { data, error } = await supabase
          .from('accommodations')
          .update(accommodationData)
          .eq('id', selectedAccommodation.id)
          .select()
          .single();
          
        if (error) throw error;
        
        setAccommodations(accommodations.map(acc => acc.id === selectedAccommodation.id ? data : acc));
      } else {
        const { data, error } = await supabase
          .from('accommodations')
          .insert(accommodationData)
          .select()
          .single();
          
        if (error) throw error;
        
        setAccommodations([...accommodations, data]);
      }
      
      setShowAddModal(false);
      setError(null);
    } catch (err) {
      console.error('Error saving accommodation:', err);
      setError(err instanceof Error ? err.message : 'Failed to save accommodation');
    }
  };

  const getTypeIcon = (type: string) => {
    return typeOptions.find(t => t.value === type)?.icon || <Hotel size={16} />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getTripTitle = (tripId: string) => {
    return trips.find(t => t.id === tripId)?.title || 'Unknown Trip';
  };

  const filteredAccommodations = accommodations.filter(acc => {
    const matchesSearch = acc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         acc.address.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || acc.type === typeFilter;
    const matchesTrip = tripId ? acc.trip_id === tripId : (tripFilter === 'all' || acc.trip_id === tripFilter);
    return matchesSearch && matchesType && matchesTrip;
  });

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Accommodations</h1>
            <p className="text-gray-600 mt-1">Manage your hotel and accommodation bookings</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="p-6">
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
                <div className="h-3 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {tripId ? 'Trip Accommodations' : 'All Accommodations'}
          </h2>
          {tripId && (
            <p className="text-gray-600 mt-1">
              Managing accommodations for this specific trip
            </p>
          )}
        </div>
        <Button
          variant="primary"
          icon={<Plus size={18} />}
          onClick={handleAddAccommodation}
        >
          Add Accommodation
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search accommodations..."
          className="flex-1"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          {typeOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        {!tripId && (
          <select
            value={tripFilter}
            onChange={(e) => setTripFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Trips</option>
            {trips.map(trip => (
              <option key={trip.id} value={trip.id}>{trip.title}</option>
            ))}
          </select>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Accommodations Grid */}
      {filteredAccommodations.length === 0 ? (
        <Card className="text-center py-12">
          <Hotel className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No accommodations found</h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || typeFilter !== 'all' || (tripId ? false : tripFilter !== 'all')
              ? 'Try adjusting your search or filters'
              : tripId 
                ? 'Start by adding your first accommodation for this trip'
                : 'Start by adding your first accommodation booking'
            }
          </p>
          {!searchQuery && typeFilter === 'all' && (tripId ? true : tripFilter === 'all') && (
            <Button
              variant="primary"
              onClick={handleAddAccommodation}
              icon={<Plus size={20} />}
            >
              {tripId ? 'Add Accommodation for This Trip' : 'Add Your First Accommodation'}
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAccommodations.map((accommodation) => (
            <Card key={accommodation.id} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{accommodation.name}</h3>
                    <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                      <MapPin size={14} />
                      {accommodation.address}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-blue-600">
                    {getTypeIcon(accommodation.type)}
                    <span className="capitalize">{accommodation.type}</span>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  {!tripId && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Trip:</span> {getTripTitle(accommodation.trip_id)}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={14} />
                    <span>{formatDate(accommodation.check_in_date)} - {formatDate(accommodation.check_out_date)}</span>
                  </div>
                  {accommodation.total_cost && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <DollarSign size={14} />
                      <span>{formatCurrency(accommodation.total_cost, accommodation.currency || 'AUD')}</span>
                    </div>
                  )}
                </div>

                {accommodation.notes && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{accommodation.notes}</p>
                )}

                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Created {formatDate(accommodation.created_at)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewAccommodation(accommodation)}
                      icon={<MapPin size={14} />}
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditAccommodation(accommodation)}
                      icon={<Edit size={14} />}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAccommodation(accommodation.id)}
                      icon={<Trash2 size={14} />}
                      className="text-red-600 hover:text-red-700"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={isEditing ? 'Edit Accommodation' : 'Add New Accommodation'}
      >
        <div className="space-y-4">
          {!tripId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trip *</label>
              <select
                value={formData.trip_id}
                onChange={(e) => setFormData({ ...formData, trip_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a trip</option>
                {trips.map(trip => (
                  <option key={trip.id} value={trip.id}>{trip.title}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Hotel/Apartment name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {currencyOptions.map(currency => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Full address"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date *</label>
              <input
                type="date"
                value={formData.check_in_date}
                onChange={(e) => setFormData({ ...formData, check_in_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date *</label>
              <input
                type="date"
                value={formData.check_out_date}
                onChange={(e) => setFormData({ ...formData, check_out_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmation Number</label>
            <input
              type="text"
              value={formData.confirmation_number}
              onChange={(e) => setFormData({ ...formData, confirmation_number: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Booking confirmation number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost</label>
            <input
              type="number"
              value={formData.total_cost}
              onChange={(e) => setFormData({ ...formData, total_cost: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Additional notes..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAddModal(false)}
            icon={<X size={16} />}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAccommodation}
            icon={<Save size={16} />}
          >
            {isEditing ? 'Update Accommodation' : 'Create Accommodation'}
          </Button>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={selectedAccommodation?.name || 'Accommodation Details'}
      >
        {selectedAccommodation && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trip</label>
                <p className="text-gray-900">{getTripTitle(selectedAccommodation.trip_id)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <div className="flex items-center gap-1 text-blue-600">
                  {getTypeIcon(selectedAccommodation.type)}
                  <span className="capitalize">{selectedAccommodation.type}</span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <p className="text-gray-900">{selectedAccommodation.address}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date</label>
                <p className="text-gray-900">{formatDate(selectedAccommodation.check_in_date)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date</label>
                <p className="text-gray-900">{formatDate(selectedAccommodation.check_out_date)}</p>
              </div>
            </div>

            {selectedAccommodation.confirmation_number && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmation Number</label>
                <p className="text-gray-900">{selectedAccommodation.confirmation_number}</p>
              </div>
            )}

            {selectedAccommodation.total_cost && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost</label>
                <p className="text-gray-900">{formatCurrency(selectedAccommodation.total_cost, selectedAccommodation.currency || 'AUD')}</p>
              </div>
            )}

            {selectedAccommodation.notes && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <p className="text-gray-900">{selectedAccommodation.notes}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                <p className="text-gray-900">{formatDate(selectedAccommodation.created_at)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                <p className="text-gray-900">{formatDate(selectedAccommodation.updated_at)}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowViewModal(false)}
          >
            Close
          </Button>
          {selectedAccommodation && (
            <Button
              variant="primary"
              onClick={() => {
                setShowViewModal(false);
                handleEditAccommodation(selectedAccommodation);
              }}
              icon={<Edit size={16} />}
            >
              Edit Accommodation
            </Button>
          )}
        </div>
      </Modal>
    </div>
  );
}
